import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

// Create context
const ThemeContext = createContext(null);

// Apply CSS custom properties to document root
const applyColors = (colors) => {
  if (!colors) return;
  const root = document.documentElement;

  // Apply to legacy --color-* variables (admin panel)
  Object.entries(colors).forEach(([key, value]) => {
    const cssVarName = `--color-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
    root.style.setProperty(cssVarName, value);
  });

  // Apply to North Star design tokens so public pages pick up the theme
  const { primary, primaryDark, primaryLight, secondary, accent } = colors;
  if (primary) {
    root.style.setProperty('--teal', primary);
    root.style.setProperty('--teal-dark', primaryDark || primary);
    root.style.setProperty('--teal-light', primaryLight || primary);
  }
  if (secondary) {
    // Override dark section backgrounds; leave --navy alone (used as text colour)
    root.style.setProperty('--navy-dark', secondary);
    root.style.setProperty('--navy-light', secondary);
  }
  if (accent) {
    root.style.setProperty('--violet', accent);
    root.style.setProperty('--violet-hover', colors.accentDark || accent);
    root.style.setProperty('--purple', accent);
    root.style.setProperty('--purple-dark', colors.accentDark || accent);
  }
};

// Provider component
export const ThemeProvider = ({ children }) => {
  const [themes, setThemes] = useState([]);
  const [activeThemeId, setActiveThemeId] = useState('default');

  // Get the current active theme object
  const activeTheme = themes.find(t => t.id === activeThemeId) || themes.find(t => t.isDefault);

  // Fetch the active theme on mount and apply it
  useEffect(() => {
    const loadTheme = async () => {
      try {
        const res = await api('/themes/active');
        if (res.theme) {
          setActiveThemeId(res.theme.id);
          setThemes((prev) => {
            const exists = prev.some((t) => t.id === res.theme.id);
            return exists
              ? prev.map((t) => (t.id === res.theme.id ? { ...t, ...res.theme } : t))
              : [...prev, res.theme];
          });

          document.documentElement.setAttribute('data-theme', res.theme.id);
          applyColors(res.theme.colors);
        }
      } catch (err) {
        console.error('Failed to load active theme:', err);
      }
    };
    loadTheme();
  }, []);

  // Apply theme to document whenever activeThemeId or activeTheme changes
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', activeThemeId);
    if (activeTheme?.colors) {
      applyColors(activeTheme.colors);
    }
  }, [activeThemeId, activeTheme]);


  // Load all themes (for admin pages)
  const loadAllThemes = useCallback(async () => {
    try {
      const res = await api('/admin/themes');
      setThemes(res.themes || []);
    } catch (err) {
      console.error('Failed to load themes:', err);
    }
  }, []);

  // Activate a theme
  const activateTheme = useCallback(async (themeId) => {
  try {
    const res = await api(`/admin/themes/${themeId}/activate`, { method: 'PUT' });
    const activatedTheme = res.theme;

    setThemes((prev) => {
      const baseThemes = prev.map((t) => ({
        ...t,
        active: t.id === themeId
      }));

      const exists = baseThemes.some((t) => t.id === themeId);

      return exists
        ? baseThemes.map((t) =>
            t.id === themeId ? { ...t, ...activatedTheme, active: true } : t
          )
        : [...baseThemes, { ...activatedTheme, active: true }];
    });

    setActiveThemeId(themeId);

    if (activatedTheme?.colors) {
      document.documentElement.setAttribute('data-theme', themeId);
      applyColors(activatedTheme.colors);
    }

    return activatedTheme;
  } catch (err) {
    console.error('Failed to activate theme:', err);
    throw err;
  }
  }, []);

  // Add a new theme
  const addTheme = useCallback(async (theme) => {
    const res = await api('/admin/themes', {
      method: 'POST',
      body: theme,
    });
    const newTheme = res.theme;
    setThemes(prev => [...prev, newTheme]);
    return newTheme;
  }, []);

  // Update a theme
  const updateTheme = useCallback(async (themeId, updates) => {
    try {
      const res = await api(`/admin/themes/${themeId}`, {
        method: 'PUT',
        body: updates,
      });
      const updatedTheme = res.theme;
      setThemes(prev => prev.map(t =>
        t.id === themeId ? { ...t, ...updatedTheme } : t
      ));

      // If updating the active theme, reapply colors
      if (themeId === activeThemeId && (updatedTheme?.colors || updates.colors)) {
        applyColors(updatedTheme?.colors || updates.colors);
      }
    } catch (err) {
      console.error('Failed to update theme:', err);
    }
  }, [activeThemeId]);

  // Delete a theme
  const deleteTheme = useCallback(async (themeId) => {
    const theme = themes.find(t => t.id === themeId);
    if (theme?.isDefault) {
      console.error('Cannot delete default theme');
      return false;
    }

    try {
      await api(`/admin/themes/${themeId}`, { method: 'DELETE' });

      // If deleting active theme, switch to default
      if (themeId === activeThemeId) {
        await activateTheme('default');
      }

      setThemes(prev => prev.filter(t => t.id !== themeId));
      return true;
    } catch (err) {
      console.error('Failed to delete theme:', err);
      return false;
    }
  }, [themes, activeThemeId, activateTheme]);

  // Reset themes — fetch fresh from API
  const resetThemes = useCallback(async () => {
    await loadAllThemes();
  }, [loadAllThemes]);

  // Get theme by ID
  const getTheme = useCallback((themeId) => {
    return themes.find(t => t.id === themeId);
  }, [themes]);

  const value = {
    themes,
    activeTheme,
    activeThemeId,
    activateTheme,
    addTheme,
    updateTheme,
    deleteTheme,
    resetThemes,
    getTheme,
    loadAllThemes
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// Custom hook to use Theme context
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
